import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import swal from 'sweetalert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheck,
  faCopy,
  faDownload,
  faMagnifyingGlass,
  faRotateLeft,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import Page from '../../components/Layout/Page';
import Loader from '../../components/Loader';
import SearchableSelect from '../../components/Inputs/SearchableSelect';
import usePageTitle from '../../hooks/usePageTitle';
import useDerivative from '../../hooks/useDerivative';
import { DerivateList } from '../../api/derivate';
import { isPreview } from '../../logic/helpers';
import settings from '../../settings';
import TranslationRow from './TranslationRow';
import FaqEditor from './FaqEditor';
import {
  buildFaqFile,
  buildFaqSummary,
  countFaqIssues,
  diffFaq,
  emptyFaqEntry,
  faqLanguages,
  moveFaqEntry,
  parseFaq,
  STANDARD_QUESTIONS,
} from './faq';
import deTranslations from '../../../lang/de';
import deChTranslations from '../../../lang/de-CH';
import enTranslations from '../../../lang/en';
import esTranslations from '../../../lang/es';
import frTranslations from '../../../lang/fr';
import itTranslations from '../../../lang/it';
import nlTranslations from '../../../lang/nl';
import {
  buildChangeSummaries,
  findDeviatingKeys,
  buildOverrideFile,
  comparePlaceholders,
  countOverrides,
  diffOverrides,
  FALLBACK_LANGUAGE,
  flattenTranslations,
  getKeyStatus,
  getSection,
  isMetaKey,
  MAIN_LANGUAGES,
  PROBLEM_STATUS,
} from './translations';

const MAIN_TRANSLATIONS = {
  de: deTranslations.translation,
  'de-CH': deChTranslations.translation,
  en: enTranslations.translation,
  es: esTranslations.translation,
  fr: frTranslations.translation,
  it: itTranslations.translation,
  nl: nlTranslations.translation,
};

const PAGE_SIZE = 100;

const FILTERS = [
  { value: 'overrides', label: 'Overrides' },
  { value: 'problems', label: 'Probleme' },
  { value: 'all', label: 'Alle' },
];

const SELECTION_KEY = 'translationEditor:selection';
const EMPTY_FAQ = { version: null, questions: [] };

const MODES = [
  { value: 'texts', label: 'Texte' },
  { value: 'faq', label: 'FAQ' },
];
const EMPTY_FILE = { base: {}, order: [], exists: true, error: null };

const flatMainCache = {};

/** Flat main translation of a language, cached because it never changes. */
function getFlatMain(language) {
  if (!flatMainCache[language]) {
    flatMainCache[language] = flattenTranslations(
      MAIN_TRANSLATIONS[language] || {},
    );
  }
  return flatMainCache[language];
}

function buildOverrideUrl(className, language) {
  const file = `${className}/${language}-translations.json`;
  const base = isPreview()
    ? 'https://conf.pendlerportal.de/files'
    : settings.cdn;
  return `${base}/${file}?ts=${Date.now()}`;
}

/** Loads one live override file, errors are part of the result. */
async function loadOverrideFile(className, language) {
  try {
    const response = await fetch(buildOverrideUrl(className, language));
    if (response.status === 404 || response.status === 403) {
      return { base: {}, order: [], exists: false, error: null };
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    const flat = flattenTranslations(json || {});
    return { base: flat, order: Object.keys(flat), exists: true, error: null };
  } catch (error) {
    return {
      base: {},
      order: [],
      exists: true,
      error:
        error instanceof SyntaxError
          ? `kein gültiges JSON (${error.message}) – im Portal greift derzeit KEIN Override dieser Sprache`
          : `nicht ladbar (${error.message})`,
    };
  }
}

function buildFaqUrl(className) {
  const base = isPreview()
    ? 'https://conf.pendlerportal.de/files'
    : settings.cdn;
  return `${base}/${className}/help.json?ts=${Date.now()}`;
}

/** Lädt die FAQ-Datei eines Portals; Fehler sind Teil des Ergebnisses. */
async function loadFaqFile(className) {
  try {
    const response = await fetch(buildFaqUrl(className));
    if (response.status === 404 || response.status === 403) {
      return { faq: EMPTY_FAQ, exists: false, error: null };
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return { faq: parseFaq(await response.json()), exists: true, error: null };
  } catch (error) {
    return {
      faq: EMPTY_FAQ,
      exists: true,
      error:
        error instanceof SyntaxError
          ? `help.json ist kein gültiges JSON (${error.message}) – im Portal greift derzeit die Standard-FAQ`
          : `help.json nicht ladbar (${error.message})`,
    };
  }
}

function draftStorageKey(className, language) {
  return `translationDraft:${className}:${language}`;
}

function faqStorageKey(className) {
  return `faqDraft:${className}`;
}

function readDraft(className, language, base) {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(draftStorageKey(className, language)),
    );
    if (parsed?.draft && diffOverrides(base, parsed.draft).count) {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

function readSelection() {
  try {
    return JSON.parse(localStorage.getItem(SELECTION_KEY)) || {};
  } catch {
    return {};
  }
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const TranslationEditor = () => {
  usePageTitle('TranslationEditor');
  const currentDerivative = useDerivative();
  const selection = useMemo(readSelection, []);
  const contextRef = useRef('');

  const [derivateList, setDerivateList] = useState([]);
  const [className, setClassName] = useState(
    selection.className || currentDerivative.className,
  );
  const [language, setLanguage] = useState(
    selection.language || FALLBACK_LANGUAGE,
  );

  /** language -> { base, order, exists, error } of the live file */
  const [files, setFiles] = useState({});
  /** language -> flat override map being edited */
  const [drafts, setDrafts] = useState({});
  /** language -> timestamp of a restored local draft */
  const [restored, setRestored] = useState({});
  const [mode, setMode] = useState('texts');
  const [faqFile, setFaqFile] = useState({
    faq: EMPTY_FAQ,
    exists: false,
    error: null,
  });
  const [faqDraft, setFaqDraft] = useState(EMPTY_FAQ);
  const [faqRestoredAt, setFaqRestoredAt] = useState(null);
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  /** Anlegen und Entfernen wirken auf alle Sprachen des Portals. */
  const [linkLanguages, setLinkLanguages] = useState(true);
  const [loadedAt, setLoadedAt] = useState(null);
  /** Portale mit ähnlichem Namen, die tatsächlich Texte haben. */
  const [siblingsWithFiles, setSiblingsWithFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('overrides');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [expanded, setExpanded] = useState({});
  const [focusPath, setFocusPath] = useState(null);
  const [showChanges, setShowChanges] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stickyOffset, setStickyOffset] = useState(0);
  const [stickyTop, setStickyTop] = useState(0);
  // Callback ref: the ContentBox around us is loaded lazily, so the toolbar
  // node only shows up after the chunk arrived.
  const [toolbarNode, setToolbarNode] = useState(null);

  useEffect(() => {
    DerivateList().then((result) => setDerivateList(result || []));
  }, []);

  /**
   * Where the toolbar has to park while scrolling, and how tall it is – the
   * column head and the section list park right below it. Two things shift it:
   * the sticky app header (which wins the stacking order, so the toolbar has to
   * stay below it) and the transform on the ContentBox, which becomes the
   * containing block for sticky children.
   *
   * Measured after every render instead of with a ResizeObserver: the observer
   * is not delivered in every embedded browser, which left the offsets stale.
   */
  const measureSticky = useCallback(() => {
    const element = toolbarNode;
    if (!element) return;

    const height = element.offsetHeight;
    setStickyOffset((current) => (current === height ? current : height));

    const box = element.closest('.ContentBox');
    const transform = box ? getComputedStyle(box).transform : 'none';
    let shift = 0;
    if (transform !== 'none' && typeof DOMMatrixReadOnly !== 'undefined') {
      try {
        shift = -new DOMMatrixReadOnly(transform).f;
      } catch {
        shift = 0;
      }
    }

    const header = document.querySelector('.Header');
    const headerHeight =
      header && getComputedStyle(header).position === 'sticky'
        ? header.offsetHeight
        : 0;
    const top = headerHeight + shift;
    setStickyTop((current) => (current === top ? current : top));
  }, [toolbarNode]);

  useLayoutEffect(() => {
    measureSticky();
  });

  useEffect(() => {
    window.addEventListener('resize', measureSticky);
    return () => window.removeEventListener('resize', measureSticky);
  }, [measureSticky]);

  const selectedDerivate = useMemo(
    () => derivateList.find((entry) => entry.className === className),
    [derivateList, className],
  );

  /** Languages the portal actually runs, everything else is a new file. */
  const portalLanguages = useMemo(() => {
    const configured = (selectedDerivate?.languages || [])
      .filter((code) => MAIN_LANGUAGES.includes(code))
      .filter((code, index, list) => list.indexOf(code) === index);
    return configured.length ? configured : [FALLBACK_LANGUAGE];
  }, [selectedDerivate]);

  /**
   * Tabs: the languages the portal is configured for, plus every language that
   * actually has a file live (267 of 274 portals have no `languages` set, so
   * the file is the more honest source). Each code appears only once – a
   * duplicate would collide as a React key and the selection would jump.
   */
  const tabLanguages = useMemo(() => {
    const list = [];
    const add = (code) => {
      if (MAIN_LANGUAGES.includes(code) && !list.includes(code)) {
        list.push(code);
      }
    };

    portalLanguages.forEach(add);
    MAIN_LANGUAGES.forEach((code) => {
      const languageFile = files[code];
      if (languageFile && (languageFile.exists || languageFile.error)) {
        add(code);
      }
    });
    add(language);
    if (showAllLanguages) MAIN_LANGUAGES.forEach(add);

    // Fixe Reihenfolge: Tabs, die beim Klick ihre Position wechseln, wirken kaputt.
    return list.sort(
      (a, b) => MAIN_LANGUAGES.indexOf(a) - MAIN_LANGUAGES.indexOf(b),
    );
  }, [portalLanguages, files, language, showAllLanguages]);

  /**
   * A new portal starts on one of its own languages – otherwise the tab of the
   * previous portal stays selected. Reacts to the portal only, so opening an
   * inactive language on purpose keeps working.
   */
  useEffect(() => {
    if (!portalLanguages.includes(language)) {
      setLanguage(portalLanguages[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalLanguages]);

  useEffect(() => {
    localStorage.setItem(
      SELECTION_KEY,
      JSON.stringify({ className, language }),
    );
  }, [className, language]);

  /**
   * One load per portal: every language at once, so the tabs know what exists
   * and switching a tab needs no further request.
   */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Alles vom vorherigen Portal sofort wegwerfen: sonst rechnen Zähler und
    // Auto-Filter noch eine Runde mit den alten Dateien.
    setFiles({});
    setDrafts({});
    setRestored({});
    setExpanded({});
    setShowChanges(false);
    setShowAllLanguages(false);

    loadFaqFile(className).then((result) => {
      if (cancelled) return;
      setFaqFile(result);
      let stored = null;
      try {
        stored = JSON.parse(localStorage.getItem(faqStorageKey(className)));
      } catch {
        stored = null;
      }
      if (stored?.faq && diffFaq(result.faq, stored.faq).count) {
        setFaqDraft(stored.faq);
        setFaqRestoredAt(stored.savedAt);
      } else {
        setFaqDraft(result.faq);
        setFaqRestoredAt(null);
      }
    });

    Promise.all(
      MAIN_LANGUAGES.map((code) =>
        loadOverrideFile(className, code).then((file) => ({ code, file })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const loadedFiles = {};
      const loadedDrafts = {};
      const restoredDrafts = {};

      results.forEach(({ code, file }) => {
        loadedFiles[code] = file;
        const stored = readDraft(className, code, file.base);
        loadedDrafts[code] = stored ? stored.draft : file.base;
        if (stored) restoredDrafts[code] = stored.savedAt;
      });

      setFiles(loadedFiles);
      setDrafts(loadedDrafts);
      setRestored(restoredDrafts);
      setLoadedAt(Date.now());
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [className]);

  /** Kein einziges Sprachfile – dann sucht der Nutzer vermutlich ein anderes Portal. */
  const hasNoFiles = useMemo(
    () =>
      Object.keys(files).length > 0 &&
      !MAIN_LANGUAGES.some((code) => files[code]?.exists || files[code]?.error),
    [files],
  );

  const portalLabel = selectedDerivate
    ? `${selectedDerivate.displayName || selectedDerivate.name || className} (${className})`
    : className;

  /**
   * Portale, deren className mit demselben Stamm beginnt – „bayer" und
   * „bayer04" liegen in der Auswahl direkt untereinander und werden verwechselt.
   */
  useEffect(() => {
    setSiblingsWithFiles([]);
    if (!hasNoFiles || !derivateList.length) return undefined;

    const stem = className.replace(/[0-9_-]+$/, '').slice(0, 6);
    if (stem.length < 3) return undefined;
    const candidates = derivateList
      .filter(
        (entry) =>
          entry.className !== className && entry.className.startsWith(stem),
      )
      .slice(0, 4);
    if (!candidates.length) return undefined;

    let cancelled = false;
    Promise.all(
      candidates.map((entry) =>
        Promise.all(
          ['de', 'en'].map((code) =>
            fetch(buildOverrideUrl(entry.className, code), { method: 'HEAD' })
              .then((response) => response.ok)
              .catch(() => false),
          ),
        ).then((results) => (results.some(Boolean) ? entry : null)),
      ),
    ).then((results) => {
      if (cancelled) return;
      setSiblingsWithFiles(results.filter(Boolean));
    });

    return () => {
      cancelled = true;
    };
  }, [hasNoFiles, derivateList, className]);

  const file = files[language] || EMPTY_FILE;
  const base = file.base;
  // memoised so the derived tables below do not recompute on every render
  const draft = useMemo(() => drafts[language] || {}, [drafts, language]);

  const diffsByLanguage = useMemo(() => {
    const map = {};
    tabLanguages.forEach((code) => {
      map[code] = diffOverrides(files[code]?.base || {}, drafts[code] || {});
    });
    return map;
  }, [tabLanguages, files, drafts]);

  const diff = diffsByLanguage[language] || { count: 0 };
  const dirtyLanguages = tabLanguages.filter(
    (code) => diffsByLanguage[code]?.count,
  );

  /** Persist the drafts so a PM can leave the page without losing work. */
  useEffect(() => {
    if (loading) return;
    tabLanguages.forEach((code) => {
      if (!files[code] || !drafts[code]) return;
      const key = draftStorageKey(className, code);
      if (diffsByLanguage[code]?.count) {
        localStorage.setItem(
          key,
          JSON.stringify({ draft: drafts[code], savedAt: Date.now() }),
        );
      } else {
        localStorage.removeItem(key);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts, diffsByLanguage, loading]);

  const flatMain = useMemo(() => getFlatMain(language), [language]);
  const flatFallback = useMemo(() => getFlatMain(FALLBACK_LANGUAGE), []);

  /**
   * All keys of this language's main translation plus every override key it
   * does not contain – including keys that only exist in the fallback language,
   * otherwise those overrides would be invisible and uncounted. Extra keys are
   * sorted into their own section so the groups stay contiguous.
   */
  const paths = useMemo(() => {
    const mainKeys = Object.keys(flatMain);
    const extra = Object.keys(draft).filter(
      (path) => !isMetaKey(path) && !(path in flatMain),
    );
    if (!extra.length) return mainKeys;

    const bySection = new Map();
    [...mainKeys, ...extra].forEach((path) => {
      const name = getSection(path);
      if (!bySection.has(name)) bySection.set(name, []);
      bySection.get(name).push(path);
    });
    return [...bySection.values()].flat();
  }, [draft, flatMain]);

  /**
   * Every other language of this portal with the info whether it overrides the
   * same key – the missing ones are the interesting part, so they stay visible.
   */
  const otherLanguagesFor = useCallback(
    (path) =>
      tabLanguages
        .filter((code) => code !== language)
        .map((code) => {
          const has = drafts[code]?.[path] !== undefined;
          return {
            code,
            has,
            dirty:
              has && (files[code]?.base || {})[path] !== drafts[code][path],
          };
        }),
    [tabLanguages, language, drafts, files],
  );

  const buildEntry = useCallback(
    (path) => {
      const hasMain = path in flatMain || path in flatFallback;
      const mainLanguage = path in flatMain ? language : FALLBACK_LANGUAGE;
      const mainValue = flatMain[path] ?? flatFallback[path] ?? '';
      const hasOverride = path in draft;
      const overrideValue = hasOverride ? draft[path] : '';

      return {
        path,
        section: getSection(path),
        hasMain,
        mainLanguage,
        mainValue,
        hasOverride,
        overrideValue,
        baseValue: base[path] ?? '',
        isNew: hasOverride && !(path in base),
        isDirty: hasOverride ? base[path] !== overrideValue : path in base,
        status: getKeyStatus({
          hasMain,
          hasOverride,
          mainValue,
          overrideValue,
        }),
        placeholders: hasOverride
          ? comparePlaceholders(mainValue, overrideValue)
          : { missing: [], added: [] },
        otherLanguages: otherLanguagesFor(path),
      };
    },
    [base, draft, flatMain, flatFallback, language, otherLanguagesFor],
  );

  /** Light weight status per key – used for counters and filters. */
  const statusByPath = useMemo(() => {
    const map = {};
    paths.forEach((path) => {
      const hasMain = path in flatMain || path in flatFallback;
      const hasOverride = path in draft;
      const mainValue = flatMain[path] ?? flatFallback[path] ?? '';
      const status = getKeyStatus({
        hasMain,
        hasOverride,
        mainValue,
        overrideValue: hasOverride ? draft[path] : '',
      });
      const placeholders = hasOverride
        ? comparePlaceholders(mainValue, draft[path])
        : { missing: [], added: [] };
      map[path] = {
        status,
        hasOverride,
        isDirty: hasOverride ? base[path] !== draft[path] : path in base,
        isProblem:
          PROBLEM_STATUS.includes(status) ||
          Boolean(placeholders.missing.length || placeholders.added.length),
      };
    });
    return map;
  }, [paths, draft, base, flatMain, flatFallback]);

  const totals = useMemo(
    () =>
      paths.reduce(
        (accumulator, path) => ({
          keys: accumulator.keys + 1,
          overrides:
            accumulator.overrides + (statusByPath[path].hasOverride ? 1 : 0),
          problems:
            accumulator.problems + (statusByPath[path].isProblem ? 1 : 0),
        }),
        { keys: 0, overrides: 0, problems: 0 },
      ),
    [paths, statusByPath],
  );

  /** A fresh portal or language starts in the view that has content. */
  useEffect(() => {
    const context = `${className}:${language}`;
    if (contextRef.current === context || !files[language]) return;
    contextRef.current = context;
    setFilter(countOverrides(drafts[language]) ? 'overrides' : 'all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [className, language, files]);

  const searchTerm = search.trim();

  const matchingPaths = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return paths.filter((path) => {
      // Eine Suche geht immer über alle Keys – sonst findet man genau die
      // Texte nicht, die man erst überschreiben will.
      if (term) {
        const haystack = `${path}\n${flatMain[path] || ''}\n${draft[path] || ''}`;
        return haystack.includes(term) || haystack.toLowerCase().includes(term);
      }

      const info = statusByPath[path];
      if (filter === 'overrides' && !info.hasOverride && !info.isDirty) {
        return false;
      }
      if (filter === 'problems' && !info.isProblem) return false;
      return true;
    });
  }, [paths, statusByPath, filter, searchTerm, flatMain, draft]);

  /** Anzahl Treffer pro Sektion – steht in der Gruppen-Kopfzeile. */
  const sectionCounts = useMemo(() => {
    const map = {};
    matchingPaths.forEach((path) => {
      const name = getSection(path);
      map[name] = (map[name] || 0) + 1;
    });
    return map;
  }, [matchingPaths]);

  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [searchTerm, filter, language]);

  const visibleEntries = useMemo(
    () => matchingPaths.slice(0, limit).map(buildEntry),
    [matchingPaths, limit, buildEntry],
  );

  const updateDrafts = useCallback((codes, updater) => {
    setDrafts((current) => {
      const next = { ...current };
      codes.forEach((code) => {
        next[code] = updater(current[code] || {}, code);
      });
      return next;
    });
  }, []);

  /** Startwert für einen neuen Override: der Haupttext dieser Sprache. */
  const mainValueFor = useCallback(
    (code, path) =>
      getFlatMain(code)[path] ?? getFlatMain(FALLBACK_LANGUAGE)[path] ?? '',
    [],
  );

  /**
   * Zielsprachen einer Aktion: eine ausdrücklich genannte Sprache, sonst alle
   * Sprachen des Portals wenn die Keys gekoppelt sind, sonst die aktuelle.
   */
  const targetLanguages = useCallback(
    (code) => {
      if (code) return [code];
      return linkLanguages && tabLanguages.length > 1
        ? tabLanguages
        : [language];
    },
    [linkLanguages, tabLanguages, language],
  );

  const handleToggle = useCallback((path, keepOpen = false) => {
    setExpanded((current) => {
      const next = { ...current };
      if (next[path] && !keepOpen) {
        delete next[path];
      } else {
        next[path] = true;
      }
      return next;
    });
    setFocusPath(keepOpen ? path : null);
  }, []);

  const handleAdopt = useCallback(
    (path, open = false, code = null) => {
      updateDrafts(targetLanguages(code), (draftOf, languageCode) => ({
        ...draftOf,
        [path]: mainValueFor(languageCode, path),
      }));
      if (open) {
        setExpanded((current) => ({ ...current, [path]: true }));
        setFocusPath(path);
      }
    },
    [mainValueFor, targetLanguages, updateDrafts],
  );

  const handleCreateEmpty = useCallback(
    (path, code = null) => {
      updateDrafts(targetLanguages(code), (draftOf) => ({
        ...draftOf,
        [path]: '',
      }));
      setFocusPath(path);
    },
    [targetLanguages, updateDrafts],
  );

  const handleChange = useCallback(
    (path, value, code = null) => {
      updateDrafts([code || language], (draftOf) => ({
        ...draftOf,
        [path]: value,
      }));
    },
    [language, updateDrafts],
  );

  const handleReset = useCallback(
    (path, code = null) => {
      updateDrafts(targetLanguages(code), (draftOf) => {
        const next = { ...draftOf };
        delete next[path];
        return next;
      });
    },
    [targetLanguages, updateDrafts],
  );

  /** Zustand eines Keys in allen Sprachen – Grundlage für den Editor. */
  const buildLanguageStates = useCallback(
    (path) =>
      tabLanguages.map((code) => {
        const flat = getFlatMain(code);
        const inLanguage = path in flat;
        const value = drafts[code]?.[path];
        const baseValue = files[code]?.base?.[path];
        return {
          code,
          mainValue: inLanguage
            ? flat[path]
            : (getFlatMain(FALLBACK_LANGUAGE)[path] ?? ''),
          mainLanguage: inLanguage ? code : FALLBACK_LANGUAGE,
          hasMain: inLanguage || path in getFlatMain(FALLBACK_LANGUAGE),
          hasOverride: value !== undefined,
          value: value ?? '',
          baseValue: baseValue ?? '',
          isNew: value !== undefined && baseValue === undefined,
          isDirty:
            value !== undefined ? baseValue !== value : baseValue !== undefined,
          error: files[code]?.error || null,
        };
      }),
    [tabLanguages, drafts, files],
  );

  /** Keys, die manche Sprachen überschreiben und andere nicht. */
  const deviatingKeys = useMemo(
    () =>
      tabLanguages.length > 1
        ? findDeviatingKeys(paths, tabLanguages, drafts)
        : [],
    [paths, tabLanguages, drafts],
  );

  const handleEvenOut = () => {
    swal({
      title: `${deviatingKeys.length} ${
        deviatingKeys.length === 1 ? 'Key' : 'Keys'
      } ausgleichen?`,
      text: `Wo eine Sprache den Key noch nicht überschreibt, wird er mit dem Standardtext dieser Sprache angelegt (${tabLanguages.join(', ')}). Danach kannst Du die Texte anpassen – nichts wird hochgeladen.`,
      buttons: ['Abbrechen', 'Anlegen'],
      icon: 'info',
    }).then((confirmed) => {
      if (!confirmed) return;
      setDrafts((current) => {
        const next = { ...current };
        tabLanguages.forEach((code) => {
          const draftOf = { ...(current[code] || {}) };
          deviatingKeys.forEach((path) => {
            if (draftOf[path] === undefined) {
              draftOf[path] = mainValueFor(code, path);
            }
          });
          next[code] = draftOf;
        });
        return next;
      });
    });
  };

  const faqDiff = useMemo(
    () => diffFaq(faqFile.faq, faqDraft),
    [faqFile, faqDraft],
  );

  const faqLangs = useMemo(
    () => faqLanguages(faqDraft, portalLanguages),
    [faqDraft, portalLanguages],
  );

  const faqIssues = useMemo(
    () => countFaqIssues(faqDraft, faqLangs),
    [faqDraft, faqLangs],
  );

  /** FAQ-Entwurf sichern, gleiche Mechanik wie bei den Texten. */
  useEffect(() => {
    if (loading) return;
    const key = faqStorageKey(className);
    if (faqDiff.count) {
      localStorage.setItem(
        key,
        JSON.stringify({ faq: faqDraft, savedAt: Date.now() }),
      );
    } else {
      localStorage.removeItem(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqDraft, faqDiff.count, loading]);

  const handleFaqField = useCallback((index, field, code, value) => {
    setFaqDraft((current) => {
      const questions = [...current.questions];
      const entry = questions[index];
      questions[index] = {
        ...entry,
        [field]: { ...entry[field], [code]: value },
      };
      return { ...current, questions };
    });
  }, []);

  const handleFaqAdd = useCallback(() => {
    setFaqDraft((current) => ({
      ...current,
      questions: [...current.questions, emptyFaqEntry(faqLangs)],
    }));
  }, [faqLangs]);

  const handleFaqRemove = useCallback((index) => {
    setFaqDraft((current) => ({
      ...current,
      questions: current.questions.filter((_, position) => position !== index),
    }));
  }, []);

  const handleFaqMove = useCallback((from, to) => {
    setFaqDraft((current) => ({
      ...current,
      questions: moveFaqEntry(current.questions, from, to),
    }));
  }, []);

  const handleFaqDownload = () => {
    const blob = new Blob(
      [`${JSON.stringify(buildFaqFile(faqDraft), null, 2)}\n`],
      {
        type: 'application/json',
      },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'help.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFaqDiscard = () => {
    setFaqDraft(faqFile.faq);
    setFaqRestoredAt(null);
    localStorage.removeItem(faqStorageKey(className));
  };

  const handleFaqCopySummary = () => {
    navigator.clipboard
      .writeText(buildFaqSummary(className, faqDiff, faqDraft))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
  };

  const handleDiscard = () => {
    setDrafts((current) => ({ ...current, [language]: base }));
    setRestored((current) => ({ ...current, [language]: null }));
    localStorage.removeItem(draftStorageKey(className, language));
  };

  const downloadLanguage = (code) => {
    const target = files[code] || EMPTY_FILE;
    const content = buildOverrideFile(
      drafts[code] || {},
      target.order,
      Object.keys(getFlatMain(code)),
    );
    const blob = new Blob([`${JSON.stringify(content, null, 2)}\n`], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${code}-translations.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadAll = () => {
    dirtyLanguages.forEach((code, index) => {
      setTimeout(() => downloadLanguage(code), index * 400);
    });
  };

  const handleCopySummary = () => {
    navigator.clipboard
      .writeText(
        buildChangeSummaries(
          className,
          dirtyLanguages.map((code) => ({
            language: code,
            diff: diffsByLanguage[code],
          })),
        ),
      )
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
  };

  const jumpToKey = (path) => {
    setSearch(path);
    setFilter('all');
    setShowChanges(false);
    setExpanded((current) => ({ ...current, [path]: true }));
  };

  const derivateOptions = useMemo(
    () =>
      [...derivateList]
        .sort((a, b) =>
          (a.displayName || a.name || a.className).localeCompare(
            b.displayName || b.name || b.className,
          ),
        )
        .map((entry) => ({
          value: entry.className,
          label: `${entry.displayName || entry.name || entry.className} (${entry.className})`,
        })),
    [derivateList],
  );

  const filePath = `derivatives/${className}/${language}-translations.json`;
  const multiLanguage = tabLanguages.length > 1;

  return (
    <div
      className="TranslationEditor"
      style={{
        '--TranslationEditor_stick': `${stickyOffset}px`,
        '--TranslationEditor_top': `${stickyTop}px`,
      }}
    >
      <Page>
        <details className="TranslationEditor-help">
          <summary>
            <h1>Translations</h1>
            <span>Wie funktionieren die zwei Ebenen?</span>
          </summary>
          <p>
            Links steht der Text aus der Haupt-Übersetzung (
            <code>src/lang/{language}.json</code>), rechts der Text, den dieses
            Portal davon abweichend benutzt. <strong>übernehmen</strong> holt
            einen Key mit dem Haupttext auf die rechte Seite, ein Klick auf eine
            Zeile öffnet sie zum Bearbeiten. Die Sprach-Tabs zeigen, wie viele
            Overrides jede Sprache hat und wo noch etwas ungespeichert ist; die
            Spalte <strong>auch in</strong> zeigt pro Key, welche anderen
            Sprachen ihn ebenfalls überschreiben. Der Download ergibt genau die
            Datei <code>{filePath}</code> für den Merge Request.
          </p>
        </details>

        <div className="TranslationEditor-toolbar" ref={setToolbarNode}>
          <div className="TranslationEditor-toolbarRow">
            <SearchableSelect
              id="translation-derivate"
              label="Portal"
              options={derivateOptions}
              value={className}
              onChange={setClassName}
              placeholder="Portal suchen..."
            />
            <div
              className="TranslationEditor-segmented"
              role="group"
              aria-label="Inhalt"
            >
              {MODES.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={mode === option.value ? 'selected' : ''}
                  aria-pressed={mode === option.value}
                  onClick={() => setMode(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {mode === 'texts' && (
              <label
                className="TranslationEditor-search"
                htmlFor="translation-search"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                <input
                  id="translation-search"
                  type="search"
                  value={search}
                  placeholder="Key oder Text suchen"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
            )}
            {mode === 'texts' && tabLanguages.length > 1 && (
              <label className="TranslationEditor-couple">
                <input
                  type="checkbox"
                  checked={linkLanguages}
                  onChange={(event) => setLinkLanguages(event.target.checked)}
                />
                Keys koppeln
                <span>{tabLanguages.join(' · ')}</span>
              </label>
            )}
            {mode === 'texts' && (
              <div
                className="TranslationEditor-segmented"
                role="group"
                aria-label="Filter"
              >
                {FILTERS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={filter === option.value ? 'selected' : ''}
                    aria-pressed={filter === option.value}
                    onClick={() => setFilter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="TranslationEditor-toolbarRow bottom">
            {mode === 'texts' && (
              <div className="TranslationEditor-tabs" role="tablist">
                {tabLanguages.map((code) => {
                  const languageFile = files[code];
                  const changes = diffsByLanguage[code]?.count || 0;
                  const overrides = countOverrides(drafts[code]);
                  const configured = portalLanguages.includes(code);
                  return (
                    <button
                      type="button"
                      role="tab"
                      key={code}
                      aria-selected={code === language}
                      className={`TranslationEditor-tab ${
                        code === language ? 'selected' : ''
                      } ${configured ? '' : 'extra'}`}
                      onClick={() => setLanguage(code)}
                      title={
                        configured
                          ? `${code}: ${overrides} Overrides`
                          : `${code} ist für dieses Portal nicht aktiv`
                      }
                    >
                      <span className="TranslationEditor-tabCode">{code}</span>
                      {languageFile &&
                        (languageFile.exists || overrides > 0) && (
                          <span className="TranslationEditor-tabCount">
                            {overrides}
                          </span>
                        )}
                      {changes > 0 && (
                        <span
                          className="TranslationEditor-tabDot"
                          title={`${changes} ungespeicherte Änderung${
                            changes === 1 ? '' : 'en'
                          }`}
                        />
                      )}
                      {languageFile?.error && (
                        <FontAwesomeIcon
                          className="TranslationEditor-tabWarn"
                          icon={faTriangleExclamation}
                          title="Live-Datei ist defekt"
                        />
                      )}
                      {languageFile && !languageFile.exists && (
                        <span
                          className="TranslationEditor-tabNew"
                          title="Datei existiert noch nicht"
                        >
                          neu
                        </span>
                      )}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="TranslationEditor-tabToggle"
                  onClick={() => setShowAllLanguages((current) => !current)}
                >
                  {showAllLanguages ? 'nur aktive' : '+ weitere Sprachen'}
                </button>
              </div>
            )}

            {mode === 'faq' ? (
              <div className="TranslationEditor-meta">
                <code>derivatives/{className}/help.json</code>
                <button
                  type="button"
                  className="TranslationEditor-metaAction neutral"
                  onClick={handleFaqDownload}
                  disabled={loading}
                  title="help.json herunterladen – mit Deinen Änderungen, sonst der Live-Stand"
                >
                  <FontAwesomeIcon icon={faDownload} /> herunterladen
                </button>
                <span>
                  {loading ? 'lädt...' : `Stand live ${formatTime(loadedAt)}`}
                </span>
                <span>
                  <strong>{faqDraft.questions.length}</strong> Fragen
                  {faqIssues > 0 && (
                    <>
                      {' · '}
                      <strong className="problem">{faqIssues}</strong>{' '}
                      unvollständig
                    </>
                  )}
                  {faqLangs.length > 0 && ` · ${faqLangs.join(' · ')}`}
                </span>
                {faqFile.error && (
                  <span className="error">{faqFile.error}</span>
                )}
                {faqRestoredAt && (
                  <span className="warn">
                    Entwurf von {formatTime(faqRestoredAt)} wiederhergestellt.
                  </span>
                )}
              </div>
            ) : (
              <div className="TranslationEditor-meta">
                <code>{filePath}</code>
                <button
                  type="button"
                  className="TranslationEditor-metaAction neutral"
                  onClick={() => downloadLanguage(language)}
                  disabled={loading}
                  title={`${language}-translations.json herunterladen – mit Deinen Änderungen, sonst der Live-Stand`}
                >
                  <FontAwesomeIcon icon={faDownload} /> herunterladen
                </button>
                <span>
                  {loading ? 'lädt...' : `Stand live ${formatTime(loadedAt)}`}
                </span>
                <span>
                  <strong>{totals.overrides}</strong> Overrides
                  {totals.problems > 0 && (
                    <>
                      {' · '}
                      <strong className="problem">
                        {totals.problems}
                      </strong>{' '}
                      Probleme
                    </>
                  )}
                  {' · '}
                  {totals.keys} Keys
                </span>
                {deviatingKeys.length > 0 && (
                  <button
                    type="button"
                    className="TranslationEditor-metaAction"
                    onClick={handleEvenOut}
                    title={`Legt die fehlenden Sprachen mit ihrem Standardtext an (${tabLanguages.join(', ')})`}
                  >
                    {deviatingKeys.length}{' '}
                    {deviatingKeys.length === 1 ? 'Key' : 'Keys'} nicht in allen
                    Sprachen – ausgleichen
                  </button>
                )}
                {file.error && <span className="error">{file.error}</span>}
                {!file.exists && !loading && (
                  <span className="warn">
                    Datei gibt es noch nicht – wird mit dem Download neu
                    angelegt.
                  </span>
                )}
                {restored[language] && (
                  <span className="warn">
                    Entwurf von {formatTime(restored[language])}{' '}
                    wiederhergestellt.
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {hasNoFiles && !loading && (
          <p className="TranslationEditor-notice">
            <strong>{portalLabel}</strong> hat in keiner Sprache eigene Texte –
            alle Texte kommen aus der Haupt-Übersetzung. Was Du hier anlegst,
            wird die erste Datei dieses Portals.
            {siblingsWithFiles.length > 0 && (
              <>
                {' '}
                Ähnlich benannt und mit vorhandenen Texten:{' '}
                {siblingsWithFiles.map((entry, index) => (
                  <React.Fragment key={entry.className}>
                    {index > 0 && ', '}
                    <button
                      type="button"
                      className="TranslationEditor-link"
                      onClick={() => setClassName(entry.className)}
                    >
                      {entry.displayName || entry.name || entry.className} (
                      {entry.className})
                    </button>
                  </React.Fragment>
                ))}
              </>
            )}
          </p>
        )}

        {showChanges && diff.count > 0 && (
          <div className="TranslationEditor-changes">
            <h3>
              {language} – {filePath}
            </h3>
            {[
              { title: 'Neu', entries: diff.added, kind: 'added' },
              { title: 'Geändert', entries: diff.changed, kind: 'changed' },
              {
                title: 'Entfernt (fällt zurück auf die Haupt-Übersetzung)',
                entries: diff.removed,
                kind: 'removed',
              },
            ]
              .filter((group) => group.entries.length)
              .map((group) => (
                <section key={group.kind}>
                  <h3>
                    {group.title} ({group.entries.length})
                  </h3>
                  <ul>
                    {group.entries.map((entry) => (
                      <li key={entry.key}>
                        <button
                          type="button"
                          className="TranslationEditor-link"
                          onClick={() => jumpToKey(entry.key)}
                        >
                          {entry.key}
                        </button>
                        <span className="TranslationEditor-changeValue">
                          {group.kind === 'changed' ? (
                            <>
                              {entry.from || '(leer)'}{' '}
                              <FontAwesomeIcon icon={faArrowRight} />{' '}
                              {entry.to || '(leer)'}
                            </>
                          ) : (
                            entry.value || '(leer)'
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
          </div>
        )}

        {mode === 'faq' ? (
          <div className="TranslationEditor-body">
            {!faqFile.exists && !loading && (
              <p className="TranslationEditor-notice">
                Dieses Portal hat noch keine <code>help.json</code>. Sobald die
                Datei existiert,{' '}
                <strong>
                  ersetzt sie die Standard-FAQ des Portals vollständig
                </strong>{' '}
                – die {STANDARD_QUESTIONS} Fragen aus der Haupt-Übersetzung
                werden dann nicht mehr angezeigt. Nimm sie also mit auf, wenn
                sie erhalten bleiben sollen.
              </p>
            )}
            {faqFile.exists && !loading && !faqFile.error && (
              <p className="TranslationEditor-notice">
                Diese Datei ersetzt im Portal die Standard-FAQ – die{' '}
                {STANDARD_QUESTIONS} Fragen aus der Haupt-Übersetzung erscheinen
                dort nicht.
              </p>
            )}
            {loading ? (
              <Loader />
            ) : (
              <FaqEditor
                questions={faqDraft.questions}
                languages={faqLangs}
                className={className}
                onChangeField={handleFaqField}
                onAddEntry={handleFaqAdd}
                onRemoveEntry={handleFaqRemove}
                onMoveEntry={handleFaqMove}
              />
            )}
          </div>
        ) : (
          <div className="TranslationEditor-body">
            <main
              className={`TranslationEditor-list ${multiLanguage ? 'multi' : ''}`}
            >
              {searchTerm && (
                <p className="TranslationEditor-searchNote">
                  {matchingPaths.length} Treffer in allen {totals.keys} Keys –
                  die Suche ignoriert den Filter.
                </p>
              )}

              <div className="TranslationEditor-headRow">
                <span>
                  Key <em>{matchingPaths.length}</em>
                </span>
                <span>Haupt · {language}</span>
                <span>
                  {className} · {language}
                </span>
                {multiLanguage && <span>Abweichung</span>}
                <span />
              </div>

              {loading && <Loader />}

              {!loading && (
                <div className="TranslationEditor-rows">
                  {visibleEntries.map((entry, index) => (
                    <React.Fragment key={entry.path}>
                      {entry.section !== visibleEntries[index - 1]?.section && (
                        <div className="TranslationEditor-group">
                          <span>{entry.section}</span>
                          <span>{sectionCounts[entry.section]}</span>
                        </div>
                      )}
                      <TranslationRow
                        entry={entry}
                        language={language}
                        className={className}
                        searchTerm={searchTerm}
                        expanded={Boolean(expanded[entry.path])}
                        focusOnOpen={focusPath === entry.path}
                        showLanguages={multiLanguage}
                        languageStates={
                          expanded[entry.path]
                            ? buildLanguageStates(entry.path)
                            : null
                        }
                        coupledLanguages={
                          linkLanguages && multiLanguage ? tabLanguages : null
                        }
                        onToggle={handleToggle}
                        onAdopt={handleAdopt}
                        onCreateEmpty={handleCreateEmpty}
                        onChange={handleChange}
                        onReset={handleReset}
                        onSelectLanguage={setLanguage}
                      />
                    </React.Fragment>
                  ))}
                </div>
              )}

              {!loading && !matchingPaths.length && (
                <div className="TranslationEditor-empty">
                  <p>Keine Keys für diese Auswahl.</p>
                  {filter !== 'all' && (
                    <button
                      type="button"
                      className="TranslationEditor-button"
                      onClick={() => setFilter('all')}
                    >
                      In allen Keys suchen
                    </button>
                  )}
                </div>
              )}

              {matchingPaths.length > visibleEntries.length && (
                <button
                  type="button"
                  className="TranslationEditor-button"
                  onClick={() => setLimit((current) => current + PAGE_SIZE)}
                >
                  weitere{' '}
                  {Math.min(
                    PAGE_SIZE,
                    matchingPaths.length - visibleEntries.length,
                  )}{' '}
                  von {matchingPaths.length} Keys anzeigen
                </button>
              )}
            </main>
          </div>
        )}

        {mode === 'faq' && faqDiff.count > 0 && (
          <div className="TranslationEditor-changebar">
            <span>
              Noch nicht im Repo: {faqDraft.questions.length} Fragen
              {faqDiff.added ? `, ${faqDiff.added} neu` : ''}
              {faqDiff.removed ? `, ${faqDiff.removed} entfernt` : ''}
              {faqDiff.edited ? `, ${faqDiff.edited} geändert` : ''}
            </span>
            <div className="TranslationEditor-changebarActions">
              <button
                type="button"
                className="TranslationEditor-button"
                onClick={handleFaqCopySummary}
                title="Zusammenfassung für die Merge-Request-Beschreibung"
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                {copied ? 'kopiert' : 'Zusammenfassung'}
              </button>
              <button
                type="button"
                className="TranslationEditor-button danger"
                onClick={handleFaqDiscard}
              >
                <FontAwesomeIcon icon={faRotateLeft} /> Verwerfen
              </button>
              <button
                type="button"
                className="TranslationEditor-button primary"
                onClick={handleFaqDownload}
              >
                <FontAwesomeIcon icon={faDownload} /> help.json herunterladen
              </button>
            </div>
          </div>
        )}

        {mode === 'texts' && dirtyLanguages.length > 0 && (
          <div className="TranslationEditor-changebar">
            <span>
              Noch nicht im Repo:{' '}
              {dirtyLanguages
                .map((code) => `${code} ${diffsByLanguage[code].count}`)
                .join(' · ')}
            </span>
            <div className="TranslationEditor-changebarActions">
              <button
                type="button"
                className="TranslationEditor-button"
                onClick={() => setShowChanges((current) => !current)}
                disabled={!diff.count}
              >
                {showChanges ? 'Liste verbergen' : `Änderungen ${language}`}
              </button>
              <button
                type="button"
                className="TranslationEditor-button"
                onClick={handleCopySummary}
                title="Zusammenfassung für die Merge-Request-Beschreibung"
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                {copied ? 'kopiert' : 'Zusammenfassung'}
              </button>
              <button
                type="button"
                className="TranslationEditor-button danger"
                onClick={handleDiscard}
                disabled={!diff.count}
              >
                <FontAwesomeIcon icon={faRotateLeft} /> {language} verwerfen
              </button>
              <button
                type="button"
                className="TranslationEditor-button primary"
                onClick={
                  dirtyLanguages.length > 1
                    ? handleDownloadAll
                    : () => downloadLanguage(dirtyLanguages[0])
                }
              >
                <FontAwesomeIcon icon={faDownload} />
                {dirtyLanguages.length > 1
                  ? `${dirtyLanguages.length} Dateien herunterladen`
                  : 'JSON herunterladen'}
              </button>
            </div>
          </div>
        )}
      </Page>
    </div>
  );
};

export default TranslationEditor;
